// ---------------------------------------------------------------------------
// Grader domain types — entity, spec, artifact-ref, and request DTOs for
// `GET/PATCH/DELETE /Graders/{name}` and `POST /grader-runs:create-judge`.
// Kept separate from `workflowApi.interface.ts` so the grader-run / variant
// types over there don't pull in unrelated grader-shape detail.
// ---------------------------------------------------------------------------

import type { VariableDeclaration } from '../types/agent.js';

/**
 * Canonical discriminator for every grader spec the backend can persist. This
 * is the single source of truth for grader-type string literals on the client:
 * it mirrors, one-for-one, the `[JsonDerivedType]` registrations on the server's
 * `GraderSpec` base class
 * (`src/services/WorkflowSvc/Common/Data/Entities/Graders/GraderSpec.cs`). Keep
 * the two lists in sync — a new backend grader type must be added here too.
 */
export type GraderType =
  | 'LlmGraderSpec'
  | 'MultiModalLlmGraderSpec'
  | 'ClassificationGraderSpec'
  | 'NumericComparisonGraderSpec'
  | 'RandomGraderSpec'
  | 'PassThroughGraderSpec'
  | 'LatencyGraderSpec'
  | 'GraderHttpProxySpec'
  | 'GraderCustomSpec'
  | 'GraderExtensionSpec'
  | 'IntermediateRequestGraderSpec'
  | 'TokenUsageGraderSpec'
  | 'TypeScriptCustomGraderSpec';

/** Human-readable label for each grader type, for display in the UI. */
export const GRADER_TYPE_LABELS: Record<GraderType, string> = {
  LlmGraderSpec: 'LLM judge',
  MultiModalLlmGraderSpec: 'Multi-modal LLM judge',
  ClassificationGraderSpec: 'Classification',
  NumericComparisonGraderSpec: 'Numeric comparison',
  RandomGraderSpec: 'Random',
  PassThroughGraderSpec: 'Pass-through',
  LatencyGraderSpec: 'Latency',
  GraderHttpProxySpec: 'HTTP proxy',
  GraderCustomSpec: 'Custom',
  GraderExtensionSpec: 'Extension',
  IntermediateRequestGraderSpec: 'Intermediate request',
  TokenUsageGraderSpec: 'Token usage',
  TypeScriptCustomGraderSpec: 'TypeScript custom',
};

/**
 * Human-readable label for a grader type. Falls back to the raw discriminator
 * for unknown/newer types the client doesn't yet have a label for.
 */
export function graderTypeLabel(type: string | undefined): string {
  if (type == null || type === '') {
    return 'Unknown';
  }
  return GRADER_TYPE_LABELS[type as GraderType] ?? type;
}

/** LLM judge specification. */
export interface JudgeGraderSpec {
  graderType: Extract<GraderType, 'LlmGraderSpec'>;
  init?: {
    modelName?: string;
    systemPrompt?: string;
    temperature?: string;
    topP?: string;
    maxTokens?: string;
  };
  /**
   * Per-invocation config. The `userMessage` is the user-message template the
   * judge runs with — populated for v2-created judges (the top candidate's
   * `prompt.userTemplate`).
   */
  invoke?: {
    userMessage?: string;
  };
}

/**
 * Grader spec that compares a numeric prediction against a numeric expected
 * value (e.g. evaluating an ordinal / Likert judge against golden labels).
 * Mirrors the server's `NumericComparisonGraderSpec`. All values are strings:
 * `init` bounds are literal numbers-as-strings; `invoke` fields are
 * template expressions resolved per trial.
 */
export interface NumericComparisonGraderSpec {
  graderType: Extract<GraderType, 'NumericComparisonGraderSpec'>;
  init?: {
    /** Minimum value of the scoring scale (e.g. `"1"` for a 1–5 Likert). */
    minScale?: string;
    /** Maximum value of the scoring scale (e.g. `"5"` for a 1–5 Likert). */
    maxScale?: string;
    /** Max distance still counting as agreement for `within_tolerance` (default `"1"`). */
    tolerance?: string;
    /** Spacing between adjacent ordinal levels for the kappa metric (default `"1"`). */
    step?: string;
  };
  invoke?: {
    /** Expected (golden / ground-truth) score expression for this trial. */
    scoreExpected?: string;
    /** Predicted score expression from the judge under test. */
    scoreActual?: string;
  };
}

/**
 * Grader spec that surfaces an agent-reported latency value as a numeric metric
 * (so it shows up in reports, trends, and A/B comparisons). Mirrors the server's
 * `LatencyGraderSpec`. It does not run a model — it grades whatever the bound
 * `invoke.latencyMs` expression resolves to (typically
 * `{{$expr:$.turn.statistics.elapsedTimeMs}}`).
 */
export interface LatencyGraderSpec {
  graderType: Extract<GraderType, 'LatencyGraderSpec'>;
  init?: {
    /** Name to emit the metric under. Defaults to `latencyMs` when omitted. */
    metricName?: string;
  };
  invoke?: {
    /**
     * Expression resolving to the latency in milliseconds for this trial. Must
     * resolve to a finite, non-negative number.
     */
    latencyMs?: string;
  };
}

/** TypeScript grader configuration; additional runtime values are preserved verbatim. */
export interface TypeScriptCustomGraderSpec {
  graderType: 'TypeScriptCustomGraderSpec';
  evaluationModel?: string;
  init: {
    modulePath: string;
    endpoint?: {
      baseUrl?: string;
      endpointType?: string;
      scenarioGuid?: string;
    };
    modelName?: string;
    timeoutSeconds?: string;
    [key: string]: unknown;
  };
  invoke?: Record<string, unknown>;
}

/**
 * Discriminated union of the grader specs the client models in detail (keyed by
 * `graderType`). The server can persist more types (see {@link GraderType}); an
 * unmodelled spec surfaces only its `graderType`, with no typed `init`/`invoke`.
 */
export type GraderSpec =
  | JudgeGraderSpec
  | NumericComparisonGraderSpec
  | LatencyGraderSpec
  | TypeScriptCustomGraderSpec;

export interface GraderCodeBundleAsset {
  bundleId: string;
  mountPath: string;
}

export interface GraderCodeBundle {
  id: string;
  files: Record<string, string>;
}

/**
 * Grader model wiring. Graders don't bake in a literal model deployment; instead
 * `init.modelName` defaults to the `GRADER_MODEL` variable reference, and the
 * eval run supplies the actual model (the grader Evaluate tab declares
 * `GRADER_MODEL` with `DEFAULT_GRADER_MODEL` and lets the user override it per
 * run). This keeps graders model-agnostic and reusable, and makes the model a
 * run-time input rather than part of the grader's identity.
 */
export const GRADER_MODEL_VARIABLE = 'GRADER_MODEL';
export const GRADER_MODEL_VAR_REF = `{{$var:${GRADER_MODEL_VARIABLE}}}`;
export const DEFAULT_GRADER_MODEL = 'gpt-4o';

/**
 * Polymorphic artifact reference. Only `inline` is supported today — the
 * payload is embedded directly in the request rather than referenced by id.
 */
export interface InlineArtifactRef {
  source: 'inline';
  data: unknown;
}

export type ArtifactRef = InlineArtifactRef;

/**
 * Response shape from `GET /Graders` / `GET /Graders/{name}` (the server's
 * `GraderResponse`).
 *
 * Fields at the top of the list (`name`, `displayName`, `spec`,
 * `systemProperties`) are the canonical server contract. The remaining fields
 * (`id`, `judgeId`, `title`, `config`, `createdAt`) are tolerated for legacy
 * payloads still in flight; delete once every caller is on the new backend.
 */
export interface ServerGrader {
  // ─── Canonical (current server) ────────────────────────────────────────
  name?: string;
  displayName?: string;
  description?: string;
  /** True when `current` has unsnapshotted edits (diverged from latest snapshot). */
  isDraft?: boolean;
  spec?: GraderSpec;
  assets?: GraderCodeBundleAsset[];
  /**
   * Free-form string→string annotations the server round-trips verbatim. The
   * Judge Factory writes `sourceGraderGenId` / `sourceGraderGenName` here when a
   * grader is promoted from a judge — see {@link GRADER_METADATA_KEYS}.
   */
  metadata?: Record<string, string>;
  variables?: Record<string, VariableDeclaration>;
  systemProperties?: {
    createdDate?: string;
    createdBy?: string;
    lastModifiedDate?: string;
    lastModifiedBy?: string;
  };

  // ─── Legacy fallbacks ──────────────────────────────────────────────────
  id?: string;
  judgeId?: string;
  title?: string;
  config?: {
    modelName?: string | null;
    systemPrompt?: string | null;
  };
  createdAt?: string;
}

/**
 * An immutable grader version snapshot, returned by
 * `GET /Graders/{name}/versions` and `GET /Graders/{name}/versions/{n}`
 * (the server's `GraderVersionResponse`). Same shape as {@link ServerGrader}
 * plus a stable `version` number; `systemProperties.createdDate` records when
 * the snapshot was frozen.
 */
export interface GraderVersion {
  name: string;
  version: number;
  displayName?: string;
  description?: string;
  spec?: GraderSpec;
  assets?: GraderCodeBundleAsset[];
  variables?: Record<string, VariableDeclaration>;
  metadata?: Record<string, string>;
  systemProperties: {
    createdDate: string;
    createdBy?: string;
    lastModifiedDate?: string;
    lastModifiedBy?: string;
  };
}

/**
 * Request payload for `PATCH /Graders/{name}`. Partial update — null/omitted
 * fields are left unchanged on the server.
 */
export interface UpdateGraderRequest {
  displayName?: string;
  description?: string;
  spec?: JudgeGraderSpec;
  variables?: Record<string, VariableDeclaration>;
}

/**
 * Request payload for `PUT /Graders/{name}` (create-or-replace). Unlike the
 * graders `POST /grader-runs:create-judge` flow, this writes the grader
 * directly with a caller-supplied spec — no golden dataset or provisioning job.
 * Used by the "create by system prompt" path (an `LlmGraderSpec`) and the
 * "numeric comparison" path (a `NumericComparisonGraderSpec`).
 */
export interface UpsertGraderRequest {
  displayName?: string;
  description?: string;
  spec?: GraderSpec;
  /** Free-form string→string annotations (see {@link GRADER_METADATA_KEYS}). */
  metadata?: Record<string, string>;
  variables?: Record<string, VariableDeclaration>;
}

/** Tuning knobs for the judge-creation orchestrator. All fields optional. */
export interface JudgeOrchestratorSettings {
  maxIterations?: number;
  convergenceThreshold?: number;
  numVariants?: number;
  topK?: number;
  usePlanning?: boolean;
  optimizationTarget?: string;
  maxEvalExamples?: number;
  /**
   * Sample size for reflection-based evaluation per iteration. When set, the
   * orchestrator samples this many examples for reflection instead of using
   * all available examples.
   */
  reflectionEvalSampleSize?: number;
  /** Enable variance calculation and reporting in the orchestrator. */
  enableVarianceCalculation?: boolean;
  /**
   * Use the structured edit proposer, which generates more targeted and
   * structured refinement suggestions.
   */
  useStructuredEditProposer?: boolean;
  /**
   * Allow major overhaul rewrites that significantly deviate from the
   * original judge.
   */
  allowMajorOverhaulRewrites?: boolean;
}

/**
 * Request payload for `POST /grader-runs:create-judge`.
 * The `config` object the old Graders:create-judge endpoint accepted
 * (modelName/systemPrompt/temperature/maxTokens) is no longer part of the
 * request — the pipeline generates those.
 */
export interface CreateGraderRequest {
  name: string;
  displayName: string;
  description: string;
  goldenDataset: ArtifactRef;
  exampleSchema?: unknown;
  orchestratorSettings?: JudgeOrchestratorSettings;
  /**
   * When `false`, dispatch the request to the v2 judge-creation pipeline
   * (worker-py `api-version >= 2026-06-10`). Defaults to the backend's `true`
   * (legacy v1) when omitted, preserving existing behavior.
   */
  useLegacy?: boolean;
}

/**
 * Per-request tuning for the validation pipeline. Each flag toggles a category
 * of validators on the worker. At least one must be `true` — the server
 * rejects requests with no categories selected.
 */
export interface JudgeValidationOptions {
  includeDataQuality?: boolean;
  includeRequirements?: boolean;
  includeMlSemantic?: boolean;
  includeRubricClarity?: boolean;
}

/**
 * Request payload for `POST /grader-runs:validate-judge`. Pre-flights the
 * description + golden dataset through the validation pipeline; no grader is
 * created.
 */
export interface ValidateJudgeRequest {
  description: string;
  goldenDataset: ArtifactRef;
  systemPrompt?: string;
  exampleSchema?: unknown;
  options: JudgeValidationOptions;
}
